import { ImageUpload } from "@/components/DesignPanel";
import { AVATAR_PRESETS } from "@/lib/api";

const inputCls = "w-full h-9 px-3 rounded-md bg-[#0B0F17] border border-[#242C3D] text-sm focus:border-indigo-500 focus:outline-none";
const label = "block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2";

export const ChatEditor = ({ chat, setChat }) => {
  const set = (patch) => setChat({ ...chat, ...patch });
  return (
    <div className="space-y-5" data-testid="chat-editor">
      <label className="flex items-center gap-3 cursor-pointer">
        <button type="button" role="switch" aria-checked={chat.enabled} data-testid="chat-enabled-toggle" onClick={() => set({ enabled: !chat.enabled })}
          className={`relative w-11 h-6 rounded-full transition-colors ${chat.enabled ? "bg-indigo-500" : "bg-[#242C3D]"}`}>
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${chat.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
        <span className="text-sm text-slate-300">{chat.enabled ? "Chat agent is on for this landing page" : "Chat agent is off"}</span>
      </label>
      {chat.enabled && (
        <>
          <div className="grid sm:grid-cols-[1fr_auto] gap-5 items-end">
            <div>
              <span className={label}>Agent name</span>
              <input data-testid="chat-name-input" value={chat.name} onChange={(e) => set({ name: e.target.value })} className={inputCls} placeholder="Event Assistant" />
            </div>
            <div>
              <span className={label}>Colour</span>
              <div className="flex items-center gap-2">
                <input type="color" value={chat.color} onChange={(e) => set({ color: e.target.value })} className="h-9 w-11 rounded-md bg-transparent border border-[#242C3D] cursor-pointer p-1" />
                <input data-testid="chat-color-input" value={chat.color} onChange={(e) => set({ color: e.target.value })} className="h-9 w-24 px-2 rounded-md bg-[#0B0F17] border border-[#242C3D] text-xs font-mono focus:outline-none" />
              </div>
            </div>
          </div>
          <div>
            <span className={label}>Avatar</span>
            <ImageUpload value={chat.avatar_path?.startsWith("/avatars/") ? "" : chat.avatar_path} onChange={(p) => set({ avatar_path: p })} testid="chat-avatar-upload" emptyLabel="Upload avatar" />
            <p className="text-xs text-slate-500 mt-3 mb-2">Or pick a ready-made avatar</p>
            <div className="flex flex-wrap gap-2" data-testid="chat-avatar-presets">
              {AVATAR_PRESETS.map((a, i) => (
                <button key={a} type="button" data-testid={`chat-avatar-preset-${i + 1}`} onClick={() => set({ avatar_path: chat.avatar_path === a ? "" : a })}
                  className={`w-12 h-12 rounded-full overflow-hidden ring-2 ring-offset-2 ring-offset-[#141A24] transition-transform hover:scale-105 ${chat.avatar_path === a ? "ring-indigo-500" : "ring-transparent"}`}>
                  <img src={a} alt={`Avatar ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className={label}>Greeting bubble (pops up next to the avatar)</span>
            <input data-testid="chat-greeting-input" maxLength={160} value={chat.greeting ?? ""} onChange={(e) => set({ greeting: e.target.value })} className={inputCls} placeholder="Hi! Got questions about this webinar?" />
            <p className="text-xs text-slate-500 mt-1.5">Shown 3 seconds after the page loads. Leave empty to hide it.</p>
          </div>
          <div>
            <span className={label}>Welcome message</span>
            <input data-testid="chat-welcome-input" value={chat.welcome} onChange={(e) => set({ welcome: e.target.value })} className={inputCls} />
          </div>
          <div>
            <span className={label}>Suggested questions (one per line, max 6)</span>
            <textarea data-testid="chat-suggestions-input" rows={3} value={chat.suggestions.join("\n")}
              onChange={(e) => set({ suggestions: e.target.value.split("\n").map((s) => s.trimStart()).slice(0, 6) })}
              onBlur={() => set({ suggestions: chat.suggestions.map((s) => s.trim()).filter(Boolean) })}
              placeholder={"Who is speaking?\nHow long is the session?\nWhat will I learn?"} className={`${inputCls} h-auto py-2 resize-none text-xs`} />
          </div>
          <div>
            <span className={label}>Knowledge for the agent</span>
            <textarea data-testid="chat-knowledge-input" rows={6} value={chat.knowledge} onChange={(e) => set({ knowledge: e.target.value })}
              placeholder="Paste FAQs, agenda, product info, pricing, contact details… The agent already knows the title, description, takeaways, speakers and session dates."
              className={`${inputCls} h-auto py-2 resize-y text-xs`} />
            <p className="text-xs text-slate-500 mt-1.5">{chat.knowledge.length.toLocaleString()} / 20,000 characters · Powered by Claude Sonnet 4.5 · Chats are not saved.</p>
          </div>
        </>
      )}
    </div>
  );
};
